<template>
  <div class="w-full">
    <header class="navbar fixed top-0 z-50 h-14 min-h-14 border-b border-base-300/60 bg-base-100/90 backdrop-blur-md px-3 lg:px-6">
      <!-- Mobile menu toggle -->
      <div class="flex-none lg:hidden">
        <button @click="toggleMenu" class="btn btn-ghost btn-sm btn-square" :aria-label="t('header.openMenu')">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="w-5 h-5 stroke-current">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      <!-- Logo -->
      <div class="flex-1">
        <router-link to="/" class="flex items-center gap-2.5 group">
          <img
            src="/logo.jpg"
            alt="ilterX"
            class="h-8 w-auto object-contain"
          />
        </router-link>
      </div>

      <!-- Actions -->
      <div class="flex-none flex items-center gap-1">
        <!-- Notifications -->
        <div v-if="isLoggedIn" class="dropdown dropdown-end">
          <button tabindex="0" class="btn btn-ghost btn-sm btn-square" :aria-label="t('header.notifications')">
            <div class="indicator">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span class="badge badge-xs badge-primary indicator-item"></span>
            </div>
          </button>
          <div tabindex="0" class="dropdown-content z-50 mt-2 w-72 rounded-xl border border-base-300 bg-base-100 p-0 shadow-lg">
            <div class="flex items-center justify-between border-b border-base-300 px-4 py-3">
              <span class="text-sm font-semibold">{{ t('header.notifications') }}</span>
              <button class="btn btn-ghost btn-xs">{{ t('header.markAllRead') }}</button>
            </div>
            <div class="max-h-56 overflow-y-auto p-2">
              <div
                v-for="(notif, i) in notifications"
                :key="i"
                class="flex cursor-pointer gap-3 rounded-lg p-2.5 hover:bg-base-200 transition-colors"
              >
                <div class="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"></div>
                <div class="min-w-0">
                  <p class="text-sm font-medium truncate">{{ notif.title }}</p>
                  <p class="text-xs text-base-content/60 truncate">{{ notif.body }}</p>
                  <p class="text-xs text-base-content/40 mt-0.5">{{ notif.time }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Guest auth -->
        <div v-if="!isLoggedIn" class="flex items-center gap-1">
          <router-link to="/users/login" class="btn btn-ghost btn-sm hidden sm:flex">
            {{ t('header.login') }}
          </router-link>
          <router-link to="/users/signIn" class="btn btn-primary btn-sm">
            {{ t('header.signUp') }}
          </router-link>
        </div>

        <!-- User dropdown -->
        <div v-if="isLoggedIn" class="dropdown dropdown-end">
          <button
            tabindex="0"
            class="flex items-center gap-2 rounded-xl border border-base-300/60 bg-base-100 pl-1 pr-2.5 py-1 hover:border-primary/30 hover:bg-base-200/50 transition-all"
          >
            <div class="avatar placeholder online">
              <div class="w-8 rounded-lg bg-gradient-to-br from-primary to-primary/70 text-primary-content text-xs font-semibold">
                <span>{{ userInitials }}</span>
              </div>
            </div>
            <div class="hidden md:block text-left min-w-0">
              <p class="text-sm font-medium leading-tight truncate max-w-[110px]">{{ displayName }}</p>
              <p class="text-[10px] text-base-content/40 leading-tight">{{ t('header.myAccount') }}</p>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 opacity-40 hidden md:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div tabindex="0" class="dropdown-content z-50 mt-2 w-72 rounded-2xl border border-base-300 bg-base-100 p-0 shadow-xl overflow-hidden">
            <!-- Profile header -->
            <div class="relative bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-4 pt-4 pb-3">
              <div class="flex items-center gap-3">
                <div class="avatar placeholder">
                  <div class="w-12 rounded-xl bg-gradient-to-br from-primary to-primary/70 text-primary-content text-base font-bold shadow-md">
                    <span>{{ userInitials }}</span>
                  </div>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="font-semibold truncate">{{ displayName }}</p>
                  <p class="text-xs text-base-content/50 truncate">{{ userData?.email }}</p>
                  <span class="badge badge-primary badge-xs mt-1">{{ t('common.roles.activeMember') }}</span>
                </div>
              </div>
            </div>

            <!-- Quick actions -->
            <div class="grid grid-cols-3 gap-1 p-2 border-b border-base-300">
              <router-link
                v-for="action in quickActions"
                :key="action.to"
                :to="action.to"
                class="flex flex-col items-center gap-1 rounded-xl p-2.5 text-center hover:bg-base-200 transition-colors group"
              >
                <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-base-200 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                  <span v-html="action.icon"></span>
                </div>
                <span class="text-[11px] font-medium text-base-content/70 group-hover:text-base-content">{{ action.label }}</span>
              </router-link>
            </div>

            <!-- Menu items -->
            <ul class="p-2 space-y-0.5">
              <li v-for="item in menuItems" :key="item.to">
                <router-link
                  :to="item.to"
                  class="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-base-200 transition-colors group"
                >
                  <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-base-200 group-hover:bg-primary/10 transition-colors">
                    <span v-html="item.icon" class="text-base-content/60 group-hover:text-primary transition-colors"></span>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium">{{ item.label }}</p>
                    <p class="text-xs text-base-content/40 truncate">{{ item.desc }}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 opacity-0 group-hover:opacity-40 transition-opacity shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </router-link>
              </li>
            </ul>

            <!-- Logout -->
            <div class="border-t border-base-300 p-2">
              <button
                @click="logOutFNC"
                class="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-error hover:bg-error/10 transition-colors group"
              >
                <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-error/10">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
                  </svg>
                </div>
                <div class="text-left">
                  <p class="text-sm font-medium">{{ t('header.logout') }}</p>
                  <p class="text-xs opacity-60">{{ t('header.logoutDesc') }}</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="h-14"></div>
  </div>
</template>

<script setup>
import { computed, defineEmits, getCurrentInstance } from "vue";
import { useRouter } from "vue-router";
import useFirebaseAuth from "../../compositions/useFirebaseAuth";
import useLocale from "../../compositions/useLocale";

const { t } = useLocale();

const app = getCurrentInstance();
const store = app.appContext.config.globalProperties.$store;
const emit = defineEmits(['toggle-menu']);
const router = useRouter();

const notifications = computed(() => [
  {
    title: t('header.notificationsSample.taskAssigned.title'),
    body: t('header.notificationsSample.taskAssigned.body'),
    time: t('header.notificationsSample.taskAssigned.time'),
  },
  {
    title: t('header.notificationsSample.meetingReminder.title'),
    body: t('header.notificationsSample.meetingReminder.body'),
    time: t('header.notificationsSample.meetingReminder.time'),
  },
]);

const iconTasks = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" /></svg>`;
const iconCalendar = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 9v7.5" /></svg>`;
const iconProfile = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg>`;
const iconEdit = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" /></svg>`;
const iconSettings = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>`;

const { user, isLoggedIn, logout } = useFirebaseAuth();

const userData = computed(() => {
  if (!user.value) return null;
  return {
    id: user.value.uid,
    email: user.value.email,
    name: user.value.displayName || '',
    username: user.value.email?.split('@')[0] || '',
  };
});

const displayName = computed(() => userData.value?.name || userData.value?.username || t('common.user'));

const userInitials = computed(() => {
  if (!userData.value) return '?';
  const n = userData.value.name?.charAt(0)?.toUpperCase() || '';
  const u = userData.value.username?.charAt(0)?.toUpperCase() || '';
  return n + u || '?';
});

const quickActions = computed(() => [
  { to: `/users/detail/${userData.value?.id}`, label: t('header.quickActions.profile'), icon: iconProfile },
  { to: '/tasks', label: 'Görevler', icon: iconTasks },
  { to: '/calendar', label: 'Takvim', icon: iconCalendar },
]);

const menuItems = computed(() => [
  {
    to: `/users/detail/${userData.value?.id}`,
    label: t('header.menuItems.myProfile.label'),
    desc: t('header.menuItems.myProfile.desc'),
    icon: iconProfile,
  },
  {
    to: `/users/update/${userData.value?.id}`,
    label: t('header.menuItems.editProfile.label'),
    desc: t('header.menuItems.editProfile.desc'),
    icon: iconEdit,
  },
  {
    to: '/settings',
    label: t('header.menuItems.settings.label'),
    desc: t('header.menuItems.settings.desc'),
    icon: iconSettings,
  },
]);

const toggleMenu = () => emit('toggle-menu');

const logOutFNC = async () => {
  try {
    await logout();
    store.commit("logoutUser");
    router.push({ name: 'HomePage' });
  } catch (error) {
    console.error('Logout failed:', error);
  }
};
</script>
